import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessionCreds, useAuth } from '../../contexts/AuthContext';
import { UpdateUser } from '../../api/api';
import { endSessionLog } from '../../utils/session';
import { useToast } from '../../components/ui';
import { normaliseText } from '../../utils/helpers';
import { passwordProblem } from '../../utils/authFlow';

// Initials for the avatar (photo upload is a future addition).
export const initials = (u) => {
  const s = `${u?.Name || ''} ${u?.Surname || ''}`.trim() || u?.MobileNumber || '?';
  return s.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
};

// All Profile logic — shared by Profile.desktop / Profile.mobile.
export function useProfile() {
  const { user, organiser, roleLabel, logout, updateUser, updateSavedCreds } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    Name: user?.Name || '',
    Surname: user?.Surname || '',
    MobileNumber: user?.MobileNumber || '',
    EmailId: user?.EmailId || '',
    Password: '',
    ConfirmPassword: '',
  });

  const doLogout = async () => {
    if (!window.confirm('Sign out on this device?')) return;
    await endSessionLog();
    logout();
    nav('/');
  };

  const setF = (key, value) => setForm(f => ({
    ...f,
    [key]: key === 'MobileNumber' ? String(value || '').replace(/\D/g, '').slice(0, 10) : value,
  }));

  const saveProfile = async () => {
    if (!form.Name.trim()) return toast('Name is required');
    if (!/^\d{10}$/.test(form.MobileNumber)) return toast('Enter a valid 10-digit mobile number');
    if (form.Password) { const problem = passwordProblem(form.Password, form.MobileNumber); if (problem) return toast(problem); }
    if (form.Password && form.Password !== form.ConfirmPassword) return toast('Passwords do not match');
    setSaving(true);
    try {
      const data = {
        Name: normaliseText(form.Name),
        Surname: normaliseText(form.Surname),
        MobileNumber: form.MobileNumber,
        EmailId: form.EmailId.trim() || null,
        UpdatedBy: user.UserId,
      };
      if (form.Password) data.Password = form.Password;
      await UpdateUser({ data, where: `UserId = ${user.UserId}` });
      updateUser({ ...user, ...data });
      const saved = getSessionCreds();
      if (saved) updateSavedCreds({ mobile: form.MobileNumber, password: form.Password || saved.password });
      setForm(f => ({ ...f, Password: '', ConfirmPassword: '' }));
      setEditing(false);
      toast('Profile updated');
    } catch {
      toast('Could not update profile');
    } finally {
      setSaving(false);
    }
  };

  const fullName = `${user?.Surname || ''} ${user?.Name || ''}`.trim();
  const goChangePassword = () => nav('/change-password');

  return {
    user, organiser, roleLabel, fullName, avatar: initials(user),
    editing, setEditing, saving, form, setF, saveProfile, doLogout, goChangePassword,
  };
}
